import React, { useEffect, useState, useRef } from "react";
import {
  Button,
  Input,
  FormControl,
  FormLabel,
  VStack,
  HStack,
  Text,
  Image, Modal, ModalOverlay, ModalContent, ModalBody, Flex, useDisclosure, ModalFooter, Box,
  ModalCloseButton,
  ModalHeader,
} from "@chakra-ui/react";
import { pxToRem } from "../../utils/editor.ts";
import { AutoComplete } from 'antd';
import { ClientError } from "../../utils/clientError.ts";
import { useTranslation } from "react-i18next";
import { PaperService, Author } from "../../services/paperService";
import { useToast } from "@chakra-ui/react"; // 导入 useToast

interface AuthorProps {
  paperId: string
  closeAuthors: () => void
}

export const Authors: React.FC<AuthorProps> = ({ closeAuthors, paperId }) => {
  const [authors, setAuthors] = useState<Author[]>([
    { name: "", affiliation: "", email: '', orcid: '', order: 0 },
  ]);
  const {
    isOpen: isOpenAdd,
    // onOpen: onOpenAdd,
    onClose: onCloseAdd,
  } = useDisclosure();
  const { t } = useTranslation()
  const authorsListRef = useRef<HTMLDivElement>(null); // 创建 ref
  const toast = useToast(); // 初始化 toast

  useEffect(() => {
    getDefaultNames()
  }, []);

 

  const addAuthor = () => {
    setAuthors([...authors, { name: "", affiliation: "", email: '', orcid: '', order: authors?.length - 1 }]);
    // 使用 setTimeout 确保在 DOM 更新后再执行滚动
    setTimeout(() => {
      if (authorsListRef.current) {
        authorsListRef.current.scrollTop = authorsListRef.current.scrollHeight;
      }
    }, 0);
  };

  const handleInputChange = (index: number, field: keyof Author, value: string) => {
    const updatedAuthors = [...authors];
    (updatedAuthors[index] as any)[field] = value;
    setAuthors(updatedAuthors);
  };

  const getDefaultNames = async () => {
    try {
      const res = await PaperService.getPaperAuthors(paperId)
      if(res.length) {
        setAuthors(res)
      }
    }  catch(error) {
      new ClientError(error).toast()
    }
    
  }

  // 保存
  const saveAuthorsFn = async() => {
    try {
      const res =  await PaperService.savePaperAuthors(paperId, authors.filter(item => item.name))
      
      if (res) { 
        toast({
          title: "Authors saved successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        closeAuthors(); 
      }
    
    } catch(error: any) {
      if (error.response && error.response.data && Array.isArray(error.response.data)) {
        const errorMessages = error.response.data.map((errItem: any) => {
          const field = Object.keys(errItem)[0];
          const messages = errItem[field];
          return `${field}: ${messages.join(', ')}`;
        }).join('\n');
        
        toast({
          title: "Error saving authors",
          description: errorMessages,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } else {
        new ClientError(error).toast(); 
      }
    }
  }

  return (

    <Modal isOpen={true} size="6xl" onClose={closeAuthors}>
      <ModalOverlay bg={'whiteAlpha.700'} />
      <ModalContent
        minW={pxToRem(900)}
        bg={'#FBFCFF'}
        boxShadow={'none'}
        border={'2px solid #D3D7E2'}
        borderRadius={pxToRem(24)}
      >
        <ModalHeader>Paper Information</ModalHeader>
        <ModalCloseButton _focusVisible={{ boxShadow: 'none' }}
          bg={'#D3D7E2'}
          width={pxToRem(24)}
          height={pxToRem(24)}
          color={'#8E9ABC'}
          top={pxToRem(20)}
          right={pxToRem(20)}
          fontSize={'8px'}
          borderRadius={'50%'}
        />
        <ModalBody>
          <VStack w={'full'} h={'full'} alignItems={'start'} mt={2}>
            <Text fontWeight="bold" fontSize={pxToRem(14)} fontFamily={'Lato'} color="#333">Paper Metadata</Text>
            <Text fontSize={pxToRem(14)} fontFamily={'Lato'} color="#8E9ABC" mb={4}>Follow the Sequence of Authorship</Text>
            <VStack w={'100%'} h={'full'} alignItems={'start'} mt={2}>
              <VStack ref={authorsListRef} w={'full'} maxH={pxToRem(430)} overflowY={'auto'} pr={pxToRem(20)} spacing={4} align="start"> 
                {authors.map((author, index) => (
                  <HStack key={index} w="full" spacing={4} align="end">
                    <Text color={'#333'} fontSize={pxToRem(16)}>{index + 1}.</Text>
                    <FormControl flex={1}>
                      <FormLabel color={'#333'} fontSize={pxToRem(16)}>Author Name</FormLabel>
                      <AutoComplete
                        value={author.name}
                        // options={options}
                        style={{ border: '1px solid #1D8AFF', width: '100%', height: '40px', borderRadius: pxToRem(6) }}
                        onSelect={(value) => handleInputChange(index, 'name', value)}
                        onChange={(value) => handleInputChange(index, 'name', value)}
                        placeholder="Author name"
                      />
                    </FormControl>
                    <FormControl flex={1}>
                      <FormLabel fontSize="sm">Affiliation(s)</FormLabel>
                      <Input
                        placeholder="Type Affiliation(s)"
                        value={author.affiliation}
                        borderColor={'#1D8AFF'}
                        borderRadius={pxToRem(6)}
                        _focus={{ borderColor: '#1D8AFF' }}
                        _focusVisible={{ borderColor: '#1D8AFF' }}
                        onChange={(e) => handleInputChange(index, "affiliation", e.target.value)}
                      // onBlur={() => {
                      //   if (index === 0) {
                      //     onOpenAdd()
                      //   }
                      // }}
                      />
                    </FormControl>
                    <FormControl flex={1}>
                      <FormLabel color={'#333'} fontSize={pxToRem(16)}>Email</FormLabel>
                      <Input
                        placeholder="Type Email"
                        value={author.email}
                        borderColor={'#1D8AFF'}
                        borderRadius={pxToRem(6)}
                        _focus={{ borderColor: '#1D8AFF' }}
                        _focusVisible={{ borderColor: '#1D8AFF' }}
                        onChange={(e) => handleInputChange(index, "email", e.target.value)} />
                    </FormControl>
                    <FormControl flex={1}>
                      <FormLabel color={'#333'} fontSize={pxToRem(16)}>ORCID</FormLabel>
                      <Input
                        placeholder="Type ORCID"
                        value={author.orcid}
                        borderColor={'#1D8AFF'}
                        borderRadius={pxToRem(6)}
                        _focus={{ borderColor: '#1D8AFF' }}
                        _focusVisible={{ borderColor: '#1D8AFF' }}
                        onChange={(e) => handleInputChange(index, "orcid", e.target.value)} />
                    </FormControl>
                  </HStack>
                ))}
              </VStack>
              <Button
                color={'#1D8AFF'}
                bg={'transparent'}
                border={'1px solid #1D8AFF'}
                borderRadius={pxToRem(6)}
                onClick={addAuthor}
                w={pxToRem(145)}
                h={pxToRem(31)}
                m={'auto'}
                mt={pxToRem(20)}
                leftIcon={
                  <Image
                    bg={'#E0EAFF'}
                    borderRadius={pxToRem(8)}
                    padding={'2px'}
                    w={pxToRem(18)} src={'/static/editor/add.svg'} />
                }>Add Author</Button>
            </VStack>
            <Modal
              isOpen={isOpenAdd}
              onClose={onCloseAdd}
              closeOnOverlayClick={false}
              size={"6xl"}
            >
              <ModalOverlay
                background={"blackAlpha.100"}
                backdropFilter={"blur(1px)"}
              />
              <ModalContent
                width={pxToRem(551)}
                height={pxToRem(345)}
                top={pxToRem(200)}
                borderRadius={pxToRem(24)}
                border={"1px solid #ccc"}
                bg={'#FBFCFF'}
              >
                <ModalBody p={5}>
                  <Flex h={'full'} w={'full'} flexDir={"column"} alignItems={'center'} justifyContent={'center'}>
                    <Image
                      w={pxToRem(56)}
                      objectFit="cover"
                      src={"/static/doenba-ico-blue.png"}
                      mb={pxToRem(24)}
                    />
                    <Text textAlign={'center'} color={'#333'} fontFamily={'Lato'} fontSize={'1rem'}>
                      Do you want to add
                      <Box as={'span'} fontWeight={'bold'} mx={2}>
                        Jason Keen, Affiliation Example
                      </Box>
                      as default author?
                      You can always edit your default author in your settings
                    </Text>
                  </Flex>
                </ModalBody>
                <ModalFooter>
                  <HStack w={'full'} justifyContent={'space-between'}>
                    <Button
                      bgColor={"#9399AB"}
                      width={pxToRem(139)}
                      height={pxToRem(48)}
                      borderRadius={pxToRem(12)}
                      textColor={"white"}
                      _hover={{ bgColor: "#1D8AFF", textColor: "white" }}
                      onClick={() => {
                        onCloseAdd();
                      }}
                    >
                      {t("editor-editormodel-leftBtnCancel")}
                    </Button>
                    <Button
                      bgColor={"#1D8AFF"}
                      width={pxToRem(139)}
                      height={pxToRem(48)}
                      borderRadius={pxToRem(12)}
                      textColor={"white"}
                      _hover={{ bgColor: "#1D8AFF", textColor: "white" }}
                      onClick={() => {
                        onCloseAdd();
                      }}
                    >
                      {t("yes-label")}
                    </Button>
                  </HStack>
                </ModalFooter>
              </ModalContent>
            </Modal>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack w={'full'} justifyContent={'space-between'} mb={8}>
            <Button
              w={pxToRem(138)}
              h={pxToRem(48)}
              bg={'#CACFDD'}
              _hover={{ background: '#CACFDD', color: '#333' }}
              border={'1px solid #CACFDD'}
              borderRadius={pxToRem(12)}
              color={'#333'}
              onClick={closeAuthors}
            >
              Back
            </Button>
            <Button
              w={pxToRem(138)}
              h={pxToRem(48)}
              bg={'#1D8AFF'}
              _hover={{ background: '#FBFCFF', color: '#1D8AFF' }}
              border={'1px solid #1D8AFF'}
              borderRadius={pxToRem(12)}
              color={'#FFFFFF'}
              onClick={() => {
                saveAuthorsFn();
              }}
            >
              Next
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};


